import { Modal } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import { useContext, useState } from "react";
import LandLordContext from "../../context/landlord/LandLordContext";

const AdditionalChecksModal = ({
  user,
  step,
  additionalInfo,
  setAdditionalInfo,
  formHandler,
  updateUserProfile,
  getNextStep,
}) => {
  const { _t } = useTranslator();
  const [submitLoading, setSubmitLoading] = useState(false);

  const isGuarantorSelected = additionalInfo.includes("TENANT_GUARANTOR");
  const hasTenantSkor = additionalInfo.includes("TENANT_SKOR");
  const hasGuarantorSkor = additionalInfo.includes("GUARANTOR_SKOR");
  const { updateAdditionalInfo } = useContext(LandLordContext);

  const lastCheckLabel = isGuarantorSelected
    ? _t("getTenantAndGuarantorSkor")
    : _t("getTenantSkor");

  /** ----------------------------
   *  Handle card toggle (Tenant KYC / Guarantor)
   * ---------------------------- */
  const toggleCheck = (key) => {
    setAdditionalInfo((prev) => {
      let updated = [...prev];

      const isSelected = updated.includes(key);

      if (isSelected) {
        // 🔻 Deselect key
        updated = updated.filter((item) => item !== key);

        // If Guarantor removed, also remove any SKOR selections
        if (key === "TENANT_GUARANTOR") {
          updated = updated.filter(
            (item) => item !== "TENANT_SKOR" && item !== "GUARANTOR_SKOR"
          );
        }
      } else {
        // ✅ Select key
        updated.push(key);

        // If Guarantor selected, remove any SKORs first
        if (key === "TENANT_GUARANTOR") {
          updated = updated.filter(
            (item) => item !== "TENANT_SKOR" && item !== "GUARANTOR_SKOR"
          );
        }
      }

      // Always keep consistency — remove GUARANTOR_SKOR if guarantor not selected
      if (!updated.includes("TENANT_GUARANTOR")) {
        updated = updated.filter((i) => i !== "GUARANTOR_SKOR");
      }

      return [...new Set(updated)];
    });
  };

  /** ----------------------------
   *  Handle SKOR selection logic
   * ---------------------------- */
  const toggleLastCheck = () => {
    setAdditionalInfo((prev) => {
      let updated = [...prev];
      const hasTenant = updated.includes("TENANT_SKOR");
      const hasGuarantor = updated.includes("GUARANTOR_SKOR");

      if (isGuarantorSelected) {
        // Both SKORs toggle together
        if (hasTenant && hasGuarantor) {
          updated = updated.filter(
            (i) => i !== "TENANT_SKOR" && i !== "GUARANTOR_SKOR"
          );
        } else {
          updated.push("TENANT_SKOR", "GUARANTOR_SKOR");
        }
      } else {
        // Only Tenant SKOR applies
        updated = hasTenant
          ? updated.filter((i) => i !== "TENANT_SKOR")
          : [...updated.filter((i) => i !== "GUARANTOR_SKOR"), "TENANT_SKOR"];
      }

      return [...new Set(updated)];
    });
  };

  /** ----------------------------
   *  Proceed to next step
   * ---------------------------- */
  const handleNextStep = async () => {
    try {
      const _step = getNextStep(step);
      setSubmitLoading(true);
      await updateAdditionalInfo({
        id: user?._id,
        additional_info: additionalInfo,
      });
      await updateUserProfile({ id: user?._id, step: _step });
    } catch (error) {
      console.log("Error", error);
    } finally {
      setSubmitLoading(true);
    }
  };

  /** ----------------------------
   *  Card definitions
   * ---------------------------- */
  const cards = [
    {
      id: "TENANT_KYC",
      img: "/images/kyc.svg",
      title: _t("tenantKyc"),
      desc: _t("nationalIdOrOtherSimilarKycDocumentsWillBeRequired"),
      onClick: () => toggleCheck("TENANT_KYC"),
      isSelected: additionalInfo.includes("TENANT_KYC"),
    },
    {
      id: "TENANT_GUARANTOR",
      img: "/images/guarantor.svg",
      title: _t("addTenantGuarantor"),
      desc: _t("guarantorInformationAndDocumentsWillBeRequired"),
      onClick: () => toggleCheck("TENANT_GUARANTOR"),
      isSelected: isGuarantorSelected,
    },
    {
      id: "TENANT_SKOR",
      img: "/images/meter.svg",
      title: lastCheckLabel,
      desc: isGuarantorSelected
        ? _t("tenantAndGuarantorBankStatementsWillBeRequired")
        : _t("tenantBankStatementsWillBeRequired"),
      onClick: toggleLastCheck,
      isSelected: hasTenantSkor || hasGuarantorSkor,
    },
  ];

  /** ----------------------------
   *  JSX
   * ---------------------------- */
  return (
    <Modal
      show
      centered
      className="landlord-onboarding-modal usage-selection-modal selectAdditionalChecks"
    >
      <Modal.Header>
        <Modal.Title>
          <h4>{_t("selectAdditionalChecksOptional")}</h4>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p>{_t("pleaseSelectAdditionalChecksYouWantToComplete")}</p>

        <div className="cards-comp">
          {cards.map((card) => (
            <div
              key={card.id}
              className={`usage-card position-relative ${card.isSelected ? "select-card" : ""}`}
              onClick={card.onClick}
            >
              <div className="checkboxWrapper d-flex align-items-center checkboxCard justify-content-center">
                <label className="custom-gap-3 custom-checkbox mb-0">
                  <input
                    type="checkbox"
                    checked={card.isSelected}
                    onChange={card.onClick}
                  />
                  <span className="custom-checkmark"></span>
                </label>
              </div>
              <img src={card.img} alt={`${card.id}-icon`} />
              <h3>{card.title}</h3>
              <p>{card.desc}</p>
            </div>
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button
          className={`footer-btn footer-back-btn ${
            additionalInfo.length === 0
              ? "lanlord-footer-btn-disabled"
              : "lanlord-footer-btn"
          }`}
          onClick={formHandler.prev}
        >
          {_t("close")}
        </button>
        <button
          disabled={submitLoading}
          className={`btnNext footer-btn`}
          onClick={handleNextStep}
        >
          {_t("next")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdditionalChecksModal;
